import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  AlertTriangle,
  Archive,
  BarChart2,
  Briefcase,
  Check,
  Clock,
  ExternalLink,
  Flag,
  HelpCircle,
  Inbox,
  LifeBuoy,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  Slash,
  UploadCloud,
  User as UserIcon,
  X,
} from "react-feather";
import { Link, useSearchParams } from "react-router";
import Select from "react-select";
import { formatDistanceToNow } from "date-fns";
import { useGet, usePost, api, apiErrorMessage } from "../../api";
import { useAuth, useSettings, settingsType } from "../../hooks";
import {
  HELP_KIND_META,
  HelpKind,
  IUser,
  IWorkPipelineBlockReason,
  IWorkPipelineListOption,
  IWorkPipelineItem,
  IWorkPipelineStopTemplateRunBody,
  IWorkPipelineRecomputeStatus,
  IWorkPipelineStageCount,
  COUPON_PERFORMANCE_LIST_NAME,
  SystemGroup,
  WorkPipelineDevStatus,
  WORK_PIPELINE_DEV_STATUS_LABELS,
  WORK_PIPELINE_DEV_HANDBACK,
  WORK_PIPELINE_DEV_STATUS_OPTIONS,
  WORK_PIPELINE_DEV_ESCALATED_LABEL,
  WORK_PIPELINE_DEV_CLAIM_STAGES,
  WORK_PIPELINE_DEV_DISABLED_REASON,
  WORK_PIPELINE_TEMPLATE_KIND_LABELS,
  WorkPipelineTemplateKind,
  devHandbackNext,
  WorkPipelineBlockState,
  WorkPipelineBrandStatus,
  WorkPipelineOutcome,
  WorkPipelineSort,
  WorkPipelineStage,
  WORK_PIPELINE_SORT_LABELS,
  WORK_PIPELINE_STAGE_LABELS,
  WORK_PIPELINE_STAGE_OWNER,
  WORK_PIPELINE_STAGE_ORDER,
} from "../../interfaces";
import { BlockReasonsModal } from "./block-reasons-modal";
import { PipelineWorkbenchDrawer } from "./pipeline-workbench-drawer";
import { CouponIssuesRunButton } from "./coupon-issues-run";
import { DevHistory } from "./dev-history";
import { TemplateDisabledTags } from "./template-disabled-tags";
import { OtherListsTag } from "./other-lists-tag";
import { DismissedAgentRequests, TemplateHelpBadge } from "./template-help-panel";
import { TemplateRunBadge } from "./template-runs-panel";
import { ManageUrgentModal, MarkUrgentModal, UrgentToggle } from "./urgent";
import { FilterBar, FilterField } from "../../components/ui";

const PAGE_SIZE = 20;

/** The board, lead queues, manager review queue, archive, and disabled-template tracking. */
type BoardView =
  | "board"
  | "urgent"
  | "mine"
  | "deals"
  | "help"
  | "review"
  | "archived"
  | "templates"
  | "agent-requests"
  | "agent-dismissed";

/** Stuck reasons the template team reviews in Template building; devs only get what they escalate. */
const STUCK_REASONS: HelpKind[] = [
  HelpKind.SIGN_IN,
  HelpKind.TEACH_JOURNEY,
  HelpKind.BOT_WALL,
  HelpKind.TEST_PRODUCT,
  HelpKind.NOT_SUITABLE,
  HelpKind.TEMPLATE,
  HelpKind.INFRA,
];

interface IBoardResponse {
  results: IWorkPipelineItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const SNAPSHOT_VARIANT: Record<string, string> = {
  present: "success",
  stale: "warning",
  missing: "secondary",
  error: "danger",
};

const STAGE_VARIANT: Record<WorkPipelineStage, string> = {
  [WorkPipelineStage.CodeHunting]: "secondary",
  [WorkPipelineStage.AwaitingSnapshot]: "info",
  [WorkPipelineStage.CodeTesting]: "primary",
  [WorkPipelineStage.CouponReview]: "danger",
  [WorkPipelineStage.Targeting]: "primary",
  [WorkPipelineStage.SessionCapture]: "warning",
  [WorkPipelineStage.TemplateBuilding]: "warning",
  [WorkPipelineStage.TemplateReview]: "warning",
  [WorkPipelineStage.ApkTesting]: "primary",
  [WorkPipelineStage.DevFixes]: "danger",
  [WorkPipelineStage.Complete]: "success",
  [WorkPipelineStage.Error]: "danger",
};

const selectStyles = {
  control: (base: any) => ({ ...base, minHeight: 31, fontSize: "0.875rem" }),
  menu: (base: any) => ({ ...base, fontSize: "0.875rem" }),
};

export const WorkPipeline: React.FC = () => {
  const { user, permissions, isAdmin } = useAuth();
  const groups = permissions?.groups ?? [];

  const isLeadTester = groups.includes(SystemGroup.LeadQaTesters);
  const isManager =
    isAdmin || isLeadTester || groups.includes(SystemGroup.QaManagers);
  const isTester = isLeadTester || groups.includes(SystemGroup.QaTesters);
  const isDeveloper = groups.includes(SystemGroup.Developers);
  // Tester access plus the developer buckets (Template building, Dev fixes).
  const isTemplateDev = groups.includes(SystemGroup.TemplateDevelopers);
  // Same set the backend allows on a Dev fixes hand-off.
  const canDevWork =
    isAdmin || isDeveloper || isTemplateDev || isManager || isLeadTester;

  const visibleStages = useMemo<WorkPipelineStage[]>(() => {
    if (isManager || (!isTester && !isDeveloper && !isTemplateDev)) {
      return WORK_PIPELINE_STAGE_ORDER;
    }
    return WORK_PIPELINE_STAGE_ORDER.filter((s) => {
      const owner = WORK_PIPELINE_STAGE_OWNER[s];
      if (owner === "none") return true;
      if ((isTester || isTemplateDev) && owner === "tester") return true;
      if ((isDeveloper || isTemplateDev) && owner === "developer") return true;
      return false;
    });
  }, [isManager, isTester, isDeveloper, isTemplateDev]);

  // ── Applied filters (persisted via settings) ─────────────
  const [stageSetting, setStageSetting] = useSettings(
    settingsType.WORK_PIPELINE_STAGE
  );
  const [claimed, setClaimed] = useSettings(settingsType.WORK_PIPELINE_CLAIMED);
  const [assignee, setAssignee] = useSettings(
    settingsType.WORK_PIPELINE_ASSIGNEE
  );
  const [country, setCountry] = useSettings(settingsType.WORK_PIPELINE_COUNTRY);
  const [search, setSearch] = useSettings(settingsType.WORK_PIPELINE_SEARCH);
  // Narrowing to one QA list applies to the stage counts too, so the tabs
  // show that list draining rather than the whole board's totals.
  const [qaListId, setQaListId] = useSettings(settingsType.WORK_PIPELINE_LIST);
  // Manually-moved rows are frozen against recompute, so being able to list
  // them is the only practical way to find what needs releasing.
  const [pinned, setPinned] = useState("");
  const [excludeAgentic, setExcludeAgentic] = useState(false);
  const [sort, setSort] = useState<WorkPipelineSort>(
    WorkPipelineSort.WaitingLongest
  );
  const [notCheckedDays, setNotCheckedDays] = useState("");
  const [stuckReason, setStuckReason] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [view, setView] = useState<BoardView>("board");
  const [archivedReason, setArchivedReason] = useState("");
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddListId, setQuickAddListId] = useState("");
  const [quickAddDomain, setQuickAddDomain] = useState("");
  const [quickAddCountry, setQuickAddCountry] = useState("");

  const defaultStage =
    visibleStages[2] ?? visibleStages[0] ?? WorkPipelineStage.CodeTesting;
  const activeStage = (
    stageSetting && visibleStages.includes(stageSetting as WorkPipelineStage)
      ? stageSetting
      : defaultStage
  ) as WorkPipelineStage;

  // ── Draft filters (edited in the bar, applied on Search) ─
  const [claimedDraft, setClaimedDraft] = useState(claimed);
  const [assigneeDraft, setAssigneeDraft] = useState(assignee);
  const [countryDraft, setCountryDraft] = useState(country);
  const [searchDraft, setSearchDraft] = useState(search);

  // ── Data ─────────────────────────────────────────────────
  // The tab badges take the board's own filters, so a badge always equals its list.
  const countsUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (excludeAgentic) p.set("excludeAgentic", "true");
    if (qaListId) p.set("qaListId", qaListId);
    if (claimed) p.set("claimed", claimed);
    if (assignee) p.set("assignee", assignee);
    if (pinned) p.set("pinned", pinned);
    if (notCheckedDays) p.set("notCheckedDays", notCheckedDays);
    if (country) p.set("countryCode", country);
    if (search) p.set("search", search);
    const qs = p.toString();
    return qs ? `work-pipeline/counts?${qs}` : "work-pipeline/counts";
  }, [
    excludeAgentic,
    qaListId,
    claimed,
    assignee,
    pinned,
    notCheckedDays,
    country,
    search,
  ]);
  const { data: counts, refetch: refetchCounts } = useGet<
    IWorkPipelineStageCount[]
  >(countsUrl, true);

  // A link from Coupon Performance shows its domain without touching the saved filters.
  const [linkFilter, setLinkFilter] = useState<{
    q: string;
    list: string;
  } | null>(() => {
    const p = new URLSearchParams(window.location.search);
    const q = p.get("q");
    return q ? { q, list: p.get("list") ?? "" } : null;
  });

  const boardUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (linkFilter) {
      p.set("page", "1");
      p.set("limit", String(PAGE_SIZE));
      p.set("search", linkFilter.q);
      if (linkFilter.list) p.set("qaListId", linkFilter.list);
      return `work-pipeline?${p.toString()}`;
    }
    if (view === "mine") {
      p.set("mine", "true");
    } else if (view === "urgent") {
      p.set("urgentOnly", "true");
    } else if (view === "agent-requests") {
      p.set("templateHelpKind", stuckReason || "any");
    } else if (view === "board") {
      if (!search && !assignee) p.set("stage", activeStage);
      if (claimed) p.set("claimed", claimed);
      if (pinned) p.set("pinned", pinned);
      if (assignee) p.set("assignee", assignee);
      if (excludeAgentic) p.set("excludeAgentic", "true");
      if (notCheckedDays) p.set("notCheckedDays", notCheckedDays);
      if (stuckReason && activeStage === WorkPipelineStage.TemplateBuilding) {
        p.set("templateHelpKind", stuckReason);
      }
    } else if (view === "review") {
      p.set("blockState", WorkPipelineBlockState.Proposed);
    } else if (view === "help") {
      p.set("escalatedOnly", "true");
    } else if (view === "deals") {
      p.set("dealMismatchOnly", "true");
    } else if (view === "templates") {
      p.set("templateDisabled", "true");
    } else {
      p.set("archived", "true");
      if (archivedReason) p.set("blockReasonCode", archivedReason);
    }
    p.set("page", String(page));
    p.set("limit", String(PAGE_SIZE));
    if (country) p.set("countryCode", country);
    if (search) p.set("search", search);
    if (qaListId) p.set("qaListId", qaListId);
    if (sort) p.set("sort", sort);
    return `work-pipeline?${p.toString()}`;
  }, [
    linkFilter,
    view,
    archivedReason,
    activeStage,
    page,
    claimed,
    assignee,
    country,
    search,
    qaListId,
    pinned,
    excludeAgentic,
    notCheckedDays,
    stuckReason,
    sort,
  ]);

  const {
    data: board,
    loading,
    refetch,
  } = useGet<IBoardResponse>(boardUrl, true);

  const { data: pipelineLists, refetch: refetchLists } = useGet<IWorkPipelineListOption[]>(
    excludeAgentic
      ? "work-pipeline/lists?excludeAgentic=true"
      : "work-pipeline/lists",
    true
  );

  // Active reasons feed the tester's picker; the manager modal loads its own
  // list including inactive ones.
  const { data: blockReasons, refetch: refetchBlockReasons } = useGet<
    IWorkPipelineBlockReason[]
  >("work-pipeline/block-reasons", true);

  const { data: reviewQueue, refetch: refetchReviewQueue } =
    useGet<IBoardResponse>(
      `work-pipeline?blockState=${WorkPipelineBlockState.Proposed}&limit=1`,
      true
    );
  const { data: helpQueue, refetch: refetchHelpQueue } = useGet<IBoardResponse>(
    isAdmin || isLeadTester ? "work-pipeline?escalatedOnly=true&limit=1" : null,
    true
  );
  const { data: myClaimed, refetch: refetchMyClaimed } = useGet<IBoardResponse>(
    user?._id ? "work-pipeline?mine=true&limit=1" : null,
    true
  );
  const { data: dealsQueue, refetch: refetchDealsQueue } =
    useGet<IBoardResponse>("work-pipeline?dealMismatchOnly=true&limit=1", true);
  const { data: templatesQueue, refetch: refetchTemplatesQueue } =
    useGet<IBoardResponse>("work-pipeline?templateDisabled=true&limit=1", true);
  const { data: urgentQueue, refetch: refetchUrgentQueue } =
    useGet<IBoardResponse>("work-pipeline?urgentOnly=true&limit=1", true);
  const { data: agentQueue, refetch: refetchAgentQueue } =
    useGet<IBoardResponse>("work-pipeline?templateHelpKind=any&limit=1", true);
  const [showUrgent, setShowUrgent] = useState(false);
  const [urgentTargets, setUrgentTargets] = useState<
    { domain: string; countryCode: string }[]
  >([]);

  const { data: users } = useGet<IUser[]>("qa-lists/users/all", true);
  const { data: refCountries } = useGet<string[]>(
    "kindred/reference/countries",
    true
  );

  const claimReq = usePost();
  const releaseReq = usePost();
  const unclaimReq = usePost();
  const outcomeReq = usePost();
  const assistanceReq = usePost();
  const leadClaimReq = usePost();
  const devReq = usePost();
  const snapshotReq = usePost();
  const moveReq = usePost();
  const reopenReq = usePost();
  const brandStatusReq = usePost();
  const blockReq = usePost();
  const stopRunReq = usePost();
  const decisionReq = usePost();
  const restoreReq = usePost();
  const reenableReq = usePost();
  const huntReq = usePost();
  const addDomainReq = usePost();

  const refreshAll = useCallback(() => {
    refetch();
    refetchCounts();
    refetchReviewQueue();
    refetchHelpQueue();
    refetchDealsQueue();
    refetchMyClaimed();
    refetchTemplatesQueue();
    refetchUrgentQueue();
    refetchAgentQueue();
    // The list banner's complete count, or it lags the stage counts beside it.
    refetchLists();
  }, [
    refetch,
    refetchCounts,
    refetchReviewQueue,
    refetchHelpQueue,
    refetchDealsQueue,
    refetchMyClaimed,
    refetchTemplatesQueue,
    refetchUrgentQueue,
    refetchAgentQueue,
    refetchLists,
  ]);

  const addDomain = async () => {
    if (!quickAddListId || !quickAddDomain.trim() || !quickAddCountry.trim()) {
      return;
    }
    try {
      await addDomainReq.execute(`qa-lists/${quickAddListId}/items`, {
        items: [
          {
            domain: quickAddDomain.trim().toLowerCase(),
            countryCode: quickAddCountry.trim().toUpperCase(),
          },
        ],
      });
      setShowQuickAdd(false);
      setQuickAddDomain("");
      setQuickAddCountry("");
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not add the domain."));
    }
  };

  const countFor = (stage: WorkPipelineStage) =>
    counts?.find((c) => c.stage === stage)?.total ?? 0;
  const urgentCountFor = (stage: WorkPipelineStage) =>
    counts?.find((c) => c.stage === stage)?.urgent ?? 0;

  // ── Filter actions ───────────────────────────────────────
  const applyFilters = () => {
    setPage(1);
    setClaimed(claimedDraft);
    setAssignee(assigneeDraft);
    setCountry(countryDraft);
    setSearch(searchDraft);
    // Settings updates re-derive boardUrl → useGet refetches automatically.
  };

  const clearFilters = () => {
    setPage(1);
    setClaimedDraft("");
    setAssigneeDraft("");
    setCountryDraft("");
    setSearchDraft("");
    setClaimed("");
    setAssignee("");
    setCountry("");
    setSearch("");
    setQaListId("");
    setPinned("");
    setExcludeAgentic(false);
    setNotCheckedDays("");
    setStuckReason("");
    setSort(WorkPipelineSort.WaitingLongest);
  };

  const showTesterClaims = () => {
    changeView("board");
    setPage(1);
    setClaimedDraft("claimed");
    setClaimed("claimed");
    setAssigneeDraft("");
    setAssignee("");
  };

  const changeStage = (stage: WorkPipelineStage) => {
    setPage(1);
    setStageSetting(stage);
  };

  const changeView = (next: BoardView) => {
    setPage(1);
    setView(next);
  };

  const activeFilterCount = [
    claimed,
    assignee,
    country,
    search,
    qaListId,
    pinned,
    excludeAgentic,
    notCheckedDays,
    stuckReason &&
      (view === "agent-requests" || activeStage === WorkPipelineStage.TemplateBuilding),
    sort !== WorkPipelineSort.WaitingLongest,
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;
  // Search and assignee span every bucket, so only a single bucket can switch the claim's meaning.
  const devClaimBucket =
    !search && !assignee && WORK_PIPELINE_DEV_CLAIM_STAGES.includes(activeStage);

  // ── Row actions ──────────────────────────────────────────
  const claim = async (id: string) => {
    try {
      await claimReq.execute(`work-pipeline/${id}/claim`);
    } catch {
      /* already claimed */
    }
    refreshAll();
  };
  // Releasing (or a lead removing a claim) asks why, so the next person knows what they pick up.
  const [releaseFor, setReleaseFor] = useState<{
    id: string;
    label: string;
    noteRequired: boolean;
    mode: "release" | "unclaim";
  } | null>(null);
  const [releaseNote, setReleaseNote] = useState("");
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const openRelease = (id: string, mode: "release" | "unclaim") => {
    const item = (board?.results ?? []).find((r) => r._id === id);
    setReleaseNote("");
    setReleaseError(null);
    setReleaseFor({
      id,
      mode,
      label: item?.brandName || item?.domain || "this domain",
      noteRequired:
        !!item?.couponReviewStep ||
        item?.qaListName === COUPON_PERFORMANCE_LIST_NAME,
    });
  };
  const release = (id: string) => openRelease(id, "release");
  const submitRelease = async () => {
    if (!releaseFor) return;
    setReleaseError(null);
    try {
      const req = releaseFor.mode === "release" ? releaseReq : unclaimReq;
      await req.execute(`work-pipeline/${releaseFor.id}/${releaseFor.mode}`, {
        note: releaseNote.trim() || undefined,
      });
      setReleaseFor(null);
      refreshAll();
    } catch (err: any) {
      setReleaseError(apiErrorMessage(err, "Could not release the claim."));
    }
  };
  const leadClaim = async (id: string) => {
    try {
      await leadClaimReq.execute(`work-pipeline/${id}/lead-claim`);
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not claim as lead."));
    }
    refreshAll();
  };
  const devAction = async (id: string, action: "dev-claim" | "dev-release") => {
    try {
      await devReq.execute(`work-pipeline/${id}/${action}`);
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not update the dev claim."));
    }
    refreshAll();
  };
  // A dev's status and comment; the hand-back ones send the row to the tester to retest.
  const [devFeedbackFor, setDevFeedbackFor] =
    useState<IWorkPipelineItem | null>(null);
  const [devStatus, setDevStatus] = useState<WorkPipelineDevStatus>(
    WorkPipelineDevStatus.InProgress
  );
  const [devFeedbackText, setDevFeedbackText] = useState("");
  const [devDisabledKinds, setDevDisabledKinds] = useState<
    WorkPipelineTemplateKind[]
  >([]);
  const disabling = devStatus === WorkPipelineDevStatus.Disabled;
  const devHandback = WORK_PIPELINE_DEV_HANDBACK.includes(devStatus);
  const [devFeedbackError, setDevFeedbackError] = useState<string | null>(null);
  const submitDevFeedback = async () => {
    if (!devFeedbackFor) return;
    setDevFeedbackError(null);
    try {
      await devReq.execute(`work-pipeline/${devFeedbackFor._id}/dev-response`, {
        status: devStatus,
        comment: devFeedbackText.trim(),
        disabledTemplates: disabling ? devDisabledKinds : [],
      });
      setDevFeedbackFor(null);
      refreshAll();
    } catch (err: any) {
      setDevFeedbackError(apiErrorMessage(err, "Could not send the feedback."));
    }
  };
  const leadRelease = async (id: string) => {
    try {
      await leadClaimReq.execute(`work-pipeline/${id}/lead-release`);
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not release the lead claim."));
    }
    refreshAll();
  };
  const [pendingOutcome, setPendingOutcome] =
    useState<WorkPipelineOutcome | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const recordOutcome = async (
    id: string,
    outcome: WorkPipelineOutcome,
    opts: {
      assistanceIssue?: string;
      devFailType?: "checkout-extension" | "apk";
      dealMismatchNote?: string;
    } = {}
  ) => {
    setPendingOutcome(outcome);
    setActionError(null);
    try {
      await outcomeReq.execute(`work-pipeline/${id}/outcome`, {
        outcome,
        assistanceIssue: opts.assistanceIssue,
        devFailType: opts.devFailType,
        dealMismatchNote: opts.dealMismatchNote,
      });
      refreshAll();
      return true;
    } catch (err: any) {
      // The api hooks throw ApiError: { message, status, data }.
      const blockers = (err?.data?.readiness?.issues ?? []).filter(
        (i: any) => i.severity === "blocker"
      );
      setActionError(
        blockers.length
          ? blockers.map((b: any) => b.message).join(" · ")
          : apiErrorMessage(err, "Could not record the outcome.")
      );
      return false;
    } finally {
      setPendingOutcome(null);
    }
  };
  const moveTo = async (id: string, stage: WorkPipelineStage | "auto") => {
    await moveReq.execute(`work-pipeline/${id}/move`, { stage });
    refreshAll();
  };
  const moveSelected = async (stage: WorkPipelineStage | "auto") => {
    for (const id of selectedIds) {
      await moveReq.execute(`work-pipeline/${id}/move`, { stage });
    }
    setSelectedIds([]);
    refreshAll();
  };
  const reopen = async (id: string) => {
    await reopenReq.execute(`work-pipeline/${id}/reopen`);
    refreshAll();
  };
  const setBrandStatus = async (
    id: string,
    brandStatus: WorkPipelineBrandStatus | null
  ) => {
    try {
      await brandStatusReq.execute(`work-pipeline/${id}/brand-status`, {
        brandStatus,
      });
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not set the brand status."));
    }
  };
  const snapshotIds = async (ids: string[]) => {
    if (!ids.length) return;
    try {
      await snapshotReq.execute("work-pipeline/add-to-snapshot", { ids });
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not run the snapshot."));
    }
  };

  // ── Block / archive workflow ─────────────────────────────
  const [blockFor, setBlockFor] = useState<IWorkPipelineItem | null>(null);
  const [blockReasonCode, setBlockReasonCode] = useState("");
  const [blockNote, setBlockNote] = useState("");
  const [rejectFor, setRejectFor] = useState<IWorkPipelineItem | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [showReasons, setShowReasons] = useState(false);

  const openBlockModal = (item: IWorkPipelineItem, reasonCode: string) => {
    setBlockFor(item);
    setBlockReasonCode(reasonCode);
    setBlockNote("");
  };

  const closeBlockModal = () => {
    setBlockFor(null);
    setBlockReasonCode("");
    setBlockNote("");
  };

  const submitBlock = async () => {
    if (!blockFor || !blockReasonCode) return;
    setActionError(null);
    try {
      await blockReq.execute(`work-pipeline/${blockFor._id}/block`, {
        reasonCode: blockReasonCode,
        note: blockNote,
      });
      closeBlockModal();
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not propose the block."));
    }
  };

  const stopTemplateRun = async (
    id: string,
    correlationId: string,
    body: IWorkPipelineStopTemplateRunBody
  ): Promise<boolean> => {
    setActionError(null);
    try {
      await stopRunReq.execute(
        `work-pipeline/${id}/template-runs/${encodeURIComponent(correlationId)}/stop`,
        body
      );
      refreshAll();
      return true;
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not stop the run."));
      return false;
    }
  };

  const decideBlock = async (id: string, approve: boolean, note?: string) => {
    setActionError(null);
    try {
      await decisionReq.execute(`work-pipeline/${id}/block/decision`, {
        approve,
        reviewNote: note ?? "",
      });
      refreshAll();
    } catch (err: any) {
      setActionError(
        apiErrorMessage(err, "Could not record the block decision.")
      );
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    await decideBlock(rejectFor._id, false, reviewNote);
    setRejectFor(null);
    setReviewNote("");
  };

  const markTemplateReenabled = async (id: string) => {
    setActionError(null);
    try {
      await reenableReq.execute(`work-pipeline/${id}/template-reenabled`);
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not clear the disabled template."));
    }
  };

  const restoreBlocked = async (id: string) => {
    setActionError(null);
    try {
      await restoreReq.execute(`work-pipeline/${id}/block/restore`);
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not restore the brand."));
    }
  };

  const recomputeReq = usePost<{ started: boolean }, any>();
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeMessage, setRecomputeMessage] = useState<string | null>(null);
  const [recomputeStatus, setRecomputeStatus] =
    useState<IWorkPipelineRecomputeStatus | null>(null);
  const recomputePollRef = useRef<number | null>(null);

  const stopRecomputePoll = useCallback(() => {
    if (recomputePollRef.current !== null) {
      window.clearInterval(recomputePollRef.current);
      recomputePollRef.current = null;
    }
  }, []);
  useEffect(() => stopRecomputePoll, [stopRecomputePoll]);

  // Recompute can touch thousands of items, so the endpoint is fire-and-forget
  // (202) and this polls status instead of blocking the request.
  const pollRecomputeStatus = useCallback(() => {
    stopRecomputePoll();
    recomputePollRef.current = window.setInterval(async () => {
      try {
        const status = await api.get<IWorkPipelineRecomputeStatus>(
          "work-pipeline/recompute/status"
        );
        setRecomputeStatus(status);
        if (status.state === "running") return;

        stopRecomputePoll();
        setRecomputing(false);
        refreshAll();
        refetchReviewQueue();
        setRecomputeMessage(
          status.state === "error"
            ? `Recompute failed: ${status.error ?? "unknown error"}`
            : status.scope === "list"
              ? `Recomputed ${status.itemsDone} item(s) for "${
                  status.listName ?? status.listId
                }".`
              : `Swept ${status.listsDone} list(s), ${status.itemsDone} item(s).`
        );
      } catch {
        // Transient poll failure — next tick retries.
      }
    }, 2000);
  }, [stopRecomputePoll]);

  const runRecompute = async (listId?: string) => {
    setRecomputeMessage(null);
    setRecomputeStatus(null);
    setRecomputing(true);
    try {
      await recomputeReq.execute("work-pipeline/recompute", { listId });
      pollRecomputeStatus();
    } catch (err: any) {
      setRecomputing(false);
      if (err?.status === 409) {
        setActionError("A recompute is already running — please wait.");
      } else {
        setActionError(apiErrorMessage(err, "Could not start the recompute."));
      }
    }
  };

  const recordHunt = async (
    id: string,
    payload: { note?: string; sourcesChecked?: string[] }
  ) => {
    setActionError(null);
    try {
      await huntReq.execute(`work-pipeline/${id}/hunt`, payload);
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not record the code hunt."));
      throw err;
    }
  };

  // ── Needs-help escalation modal ──────────────────────────
  const [helpFor, setHelpFor] = useState<IWorkPipelineItem | null>(null);
  const [helpIssue, setHelpIssue] = useState("");
  const [feedbackFor, setFeedbackFor] = useState<IWorkPipelineItem | null>(
    null
  );
  const [feedbackQueue, setFeedbackQueue] = useState<"help" | "deals">("help");
  const [assistanceFeedback, setAssistanceFeedback] = useState("");
  const [dealMismatchFor, setDealMismatchFor] =
    useState<IWorkPipelineItem | null>(null);
  const [dealMismatchNote, setDealMismatchNote] = useState("");
  const submitHelp = async () => {
    if (!helpFor) return;
    const saved = await recordOutcome(
      helpFor._id,
      WorkPipelineOutcome.NeedsHelp,
      {
        assistanceIssue: helpIssue.trim(),
      }
    );
    if (!saved) return;
    setHelpFor(null);
    setHelpIssue("");
  };
  const submitAssistanceFeedback = async () => {
    if (!feedbackFor || !assistanceFeedback.trim()) return;
    try {
      await assistanceReq.execute(
        `work-pipeline/${feedbackFor._id}/${
          feedbackQueue === "deals" ? "deals-response" : "assistance-response"
        }`,
        { feedback: assistanceFeedback.trim() }
      );
      setFeedbackFor(null);
      setAssistanceFeedback("");
      refreshAll();
    } catch (err: any) {
      setActionError(apiErrorMessage(err, "Could not send feedback."));
    }
  };
  const submitDealMismatch = async () => {
    if (!dealMismatchFor || !dealMismatchNote.trim()) return;
    await recordOutcome(dealMismatchFor._id, WorkPipelineOutcome.DealMismatch, {
      dealMismatchNote: dealMismatchNote.trim(),
    });
    setDealMismatchFor(null);
    setDealMismatchNote("");
  };

  // ── Inline workbench drawer ──────────────────────────────
  // Held in the query string so a browser refresh reopens the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const drawerId = searchParams.get("item");
  const drawerItem = drawerId
    ? ((board?.results ?? []).find((r) => r._id === drawerId) ?? null)
    : null;

  // The link's q/list only seed the one-off filter; drop them from the address bar.
  useEffect(() => {
    if (!searchParams.get("q")) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("q");
        next.delete("list");
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDrawerParams = useCallback(
    (replace: boolean) => {
      setLinkFilter(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of ["item", "entity", "entityType", "tab"]) {
            next.delete(key);
          }
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  const openDrawer = (id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("item", id);
      next.delete("entity");
      next.delete("entityType");
      next.delete("tab");
      return next;
    });
  };

  // There is no single-item endpoint, so an `item` that isn't on this page of
  // results (stale link, different page/filters) is dropped rather than
  // opening an empty drawer.
  useEffect(() => {
    if (!drawerId || loading || !board) return;
    if (!board.results.some((r) => r._id === drawerId)) clearDrawerParams(true);
  }, [drawerId, loading, board, clearDrawerParams]);


  const activeReasons = (blockReasons ?? []).filter((r) => r.isActive);
  const pendingReviewCount = reviewQueue?.pagination?.total ?? 0;
  const helpQueueCount = helpQueue?.pagination?.total ?? 0;

  // Distinct lists represented on the current page, for a targeted recompute.
  const boardListIds = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of board?.results ?? []) {
      if (!seen.has(r.qaListId)) seen.set(r.qaListId, r.qaListName);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [board]);

  const listOptions = (pipelineLists ?? []).map((l) => ({
    value: l.qaListId,
    label: `${l.qaListName} (${l.complete}/${l.total} complete)`,
  }));
  const selectedList = (pipelineLists ?? []).find(
    (l) => l.qaListId === qaListId
  );
  // The filter persists between sessions, so it can outlive the list. Say so
  // rather than showing an empty board with the picker reading "All lists".
  const staleListFilter = !!qaListId && !!pipelineLists && !selectedList;

  const userOptions = (users ?? []).map((u) => ({
    value: u._id.toString(),
    label: u.username,
  }));
  const countryOptions = (refCountries ?? []).map((c) => ({
    value: c,
    label: c,
  }));

  const pag = board?.pagination;

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Work Pipeline</h4>
        <div className="d-flex gap-2">
          {isAdmin || groups.includes(SystemGroup.QaManagers) ? (
            <Link
              to="/work-pipeline/productivity"
              className="btn btn-sm btn-outline-secondary"
            >
              Productivity
            </Link>
          ) : null}
          <Link
            to="/work-pipeline/report"
            className="btn btn-sm btn-outline-secondary"
          >
            Report
          </Link>
          <Link
            to="/work-pipeline/audit"
            className="btn btn-sm btn-outline-secondary"
          >
            Audit Log
          </Link>
          {isManager && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowReasons(true)}
            >
              <Settings size={14} className="me-1" /> Block reasons
            </Button>
          )}
          {isManager && <CouponIssuesRunButton onApplied={refreshAll} />}
          {isManager && (
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                disabled={recomputing}
                id="wp-recompute"
              >
                {recomputing ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : (
                  <RefreshCw size={14} className="me-1" />
                )}
                {recomputing
                  ? recomputeStatus
                    ? `Recomputing ${recomputeStatus.itemsDone}/${
                        recomputeStatus.itemsTotal || "?"
                      }…`
                    : "Starting…"
                  : "Recompute"}
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                {boardListIds.map((l) => (
                  <Dropdown.Item key={l.id} onClick={() => runRecompute(l.id)}>
                    {l.name}
                  </Dropdown.Item>
                ))}
                {boardListIds.length > 0 && <Dropdown.Divider />}
                <Dropdown.Item onClick={() => runRecompute()}>
                  Full sweep (every pipeline list)
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
          <Button variant="outline-secondary" size="sm" onClick={refreshAll}>
            <RefreshCw size={14} className="me-1" /> Refresh
          </Button>
          {(isTester || isManager) && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setQuickAddListId(boardListIds[0]?.id ?? "");
                setShowQuickAdd(true);
              }}
            >
              Add domain
            </Button>
          )}
        </div>
      </div>

      <Row className="g-3">
        <Col lg={2}>
          <div className="d-grid gap-1">
            <small className="text-uppercase text-muted fw-semibold mb-1">
              Board
            </small>
            <Button
              size="sm"
              className="text-start"
              variant={view === "board" ? "primary" : "light"}
              onClick={() => changeView("board")}
            >
              Board
            </Button>
            <Button
              size="sm"
              className="text-start d-flex justify-content-between align-items-center"
              variant={view === "urgent" ? "danger" : "light"}
              onClick={() => changeView("urgent")}
              title="Domains a lead marked urgent, across every bucket. They also sit at the top of each bucket."
            >
              <span>
                <Flag size={13} className="me-1" /> Urgent
              </span>
              <Badge
                bg={urgentQueue?.pagination?.total ? "danger" : "light"}
                text={urgentQueue?.pagination?.total ? undefined : "dark"}
                className={view === "urgent" ? "border border-light" : undefined}
              >
                {urgentQueue?.pagination?.total ?? 0}
              </Badge>
            </Button>
            {visibleStages.map((stage) => (
              <Button
                key={stage}
                size="sm"
                className="text-start d-flex justify-content-between align-items-center"
                variant={
                  view === "board" && stage === activeStage
                    ? STAGE_VARIANT[stage]
                    : "light"
                }
                onClick={() => {
                  changeView("board");
                  changeStage(stage);
                }}
              >
                {WORK_PIPELINE_STAGE_LABELS[stage]}
                <span className="d-inline-flex gap-1">
                  {urgentCountFor(stage) > 0 && (
                    <Badge bg="danger" title={`${urgentCountFor(stage)} urgent`}>
                      <Flag size={9} /> {urgentCountFor(stage)}
                    </Badge>
                  )}
                  <Badge bg="light" text="dark">
                    {countFor(stage)}
                  </Badge>
                </span>
              </Button>
            ))}
            <Button
              size="sm"
              className="text-start d-flex justify-content-between align-items-center"
              variant={view === "agent-requests" ? "warning" : "light"}
              onClick={() => changeView("agent-requests")}
              title="Every merchant the agent has an open request for, in any bucket and on any list. Rows stay in their own bucket; this queue is so none are missed."
            >
              <span>
                <LifeBuoy size={13} className="me-1" /> Agent requests
              </span>
              <Badge bg="light" text="dark">
                {agentQueue?.pagination?.total ?? 0}
              </Badge>
            </Button>
            <Button
              size="sm"
              className="text-start d-flex justify-content-between align-items-center"
              variant={view === "deals" ? "warning" : "light"}
              onClick={() => changeView("deals")}
            >
              <span>
                <AlertTriangle size={13} className="me-1" /> Deals queue
              </span>
              <Badge bg="light" text="dark">
                {dealsQueue?.pagination?.total ?? 0}
              </Badge>
            </Button>
          </div>
          {(isTester || canDevWork) && (
            <div className="d-grid gap-1 mt-4">
              <small className="text-uppercase text-muted fw-semibold mb-1">
                My work today
              </small>
              <Button
                size="sm"
                className="text-start d-flex justify-content-between align-items-center"
                variant={view === "mine" ? "primary" : "light"}
                onClick={() => changeView("mine")}
              >
                <span
                  title={
                    isDeveloper || isTemplateDev
                      ? "Domains you hold as tester or dev, plus fixes you sent back that are still being retested"
                      : "Domains you currently hold"
                  }
                >
                  <Briefcase size={13} className="me-1" /> Claimed domains
                </span>
                <Badge bg="light" text="dark">
                  {myClaimed?.pagination.total ?? 0}
                </Badge>
              </Button>
              <Link
                to="/work-pipeline/productivity?today=true"
                className="btn btn-sm btn-light text-start"
              >
                <BarChart2 size={13} className="me-1" /> Productivity today
              </Link>
            </div>
          )}
          <div className="d-grid gap-1 mt-4">
            <small className="text-uppercase text-muted fw-semibold mb-1">
              Management
            </small>
            <Button
              size="sm"
              className="text-start"
              variant="light"
              onClick={() => setShowUrgent(true)}
              title={
                isManager
                  ? "See every urgent flag, add a pasted list of domains, or take flags off"
                  : "See every domain a lead marked urgent and why"
              }
            >
              <Flag size={13} className="me-1" /> Urgent domains
            </Button>
            {isManager && (
              <Button
                size="sm"
                className="text-start"
                variant="light"
                onClick={showTesterClaims}
              >
                <UserIcon size={13} className="me-1" /> Tester claims
              </Button>
            )}
            {(isAdmin || isLeadTester) && (
              <Button
                size="sm"
                className="text-start d-flex justify-content-between align-items-center"
                variant={view === "help" ? "primary" : "light"}
                onClick={() => changeView("help")}
              >
                <span>
                  <HelpCircle size={13} className="me-1" /> Help queue
                </span>
                <Badge bg="light" text="dark">
                  {helpQueueCount}
                </Badge>
              </Button>
            )}
            <Button
              size="sm"
              className="text-start d-flex justify-content-between align-items-center"
              variant={view === "review" ? "warning" : "light"}
              onClick={() => changeView("review")}
            >
              <span>
                <Inbox size={13} className="me-1" /> Block review
              </span>
              <Badge bg="light" text="dark">
                {pendingReviewCount}
              </Badge>
            </Button>
            <Button
              size="sm"
              className="text-start d-flex justify-content-between align-items-center"
              variant={view === "templates" ? "dark" : "light"}
              onClick={() => changeView("templates")}
              title="Domains whose auto or SKU template is disabled in SQL. Tracking only: they stay on the board and are worked as normal."
            >
              <span>
                <Slash size={13} className="me-1" /> Disabled templates
              </span>
              <Badge bg="light" text="dark">
                {templatesQueue?.pagination?.total ?? 0}
              </Badge>
            </Button>
            <Button
              size="sm"
              className="text-start"
              variant={view === "archived" ? "secondary" : "light"}
              onClick={() => changeView("archived")}
            >
              <Archive size={13} className="me-1" /> Archived
            </Button>
            {isManager && (
              <Button
                size="sm"
                className="text-start"
                variant={view === "agent-dismissed" ? "secondary" : "light"}
                onClick={() => changeView("agent-dismissed")}
                title="Agent requests a person dismissed. Their rows left the board; reopen one to put it back in Template building."
              >
                <LifeBuoy size={13} className="me-1" /> Agent dismissed
              </Button>
            )}
          </div>
        </Col>
        <Col lg={10}>
          {view === "board" && isManager && selectedIds.length > 0 && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <small className="text-muted">
                {selectedIds.length} domain{selectedIds.length === 1 ? "" : "s"}{" "}
                selected
              </small>
              <Form.Select
                size="sm"
                style={{ width: 220 }}
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value)
                    moveSelected(
                      e.target.value as WorkPipelineStage | "auto"
                    );
                  e.currentTarget.value = "";
                }}
                disabled={moveReq.loading}
              >
                <option value="">Move selected to...</option>
                {WORK_PIPELINE_STAGE_ORDER.filter(
                  (stage) => stage !== activeStage
                ).map((stage) => (
                  <option key={stage} value={stage}>
                    {WORK_PIPELINE_STAGE_LABELS[stage]}
                  </option>
                ))}
                <option value="auto">
                  ↩ Return to auto (unpin, re-derive)
                </option>
              </Form.Select>
              <Button
                size="sm"
                variant="outline-danger"
                title="Put the selected domains at the top of every bucket for testers and devs"
                onClick={() =>
                  setUrgentTargets(
                    (board?.results ?? [])
                      .filter((r) => selectedIds.includes(r._id))
                      .map((r) => ({ domain: r.domain, countryCode: r.countryCode }))
                  )
                }
              >
                <Flag size={13} className="me-1" /> Mark urgent
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setSelectedIds([])}
              >
                Clear
              </Button>
            </div>
          )}

          <FilterBar
            onSearch={applyFilters}
            onClear={clearFilters}
            activeCount={activeFilterCount}
          >
            {view === "board" && (
              <>
                <FilterField
                  label="Claimed"
                  md={2}
                  tooltip={
                    devClaimBucket
                      ? "In Dev fixes this is the dev's claim. The tester who escalated it keeps their own claim to retest; that one doesn't count here."
                      : "The tester's claim in this bucket. In Dev fixes it switches to the dev's claim."
                  }
                >
                  <Form.Select
                    size="sm"
                    value={claimedDraft}
                    onChange={(e) => setClaimedDraft(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="claimed">
                      {devClaimBucket ? "Claimed by a dev" : "Claimed"}
                    </option>
                    <option value="unclaimed">
                      {devClaimBucket ? "No dev yet" : "Unclaimed"}
                    </option>
                  </Form.Select>
                </FilterField>
                <FilterField
                  label="Assignee"
                  md={3}
                  tooltip="Rows this tester holds, across every bucket."
                >
                  <Select
                    options={userOptions}
                    value={
                      assigneeDraft
                        ? (userOptions.find((o) => o.value === assigneeDraft) ??
                          null)
                        : null
                    }
                    onChange={(o) => setAssigneeDraft(o?.value ?? "")}
                    isClearable
                    placeholder="Anyone…"
                    styles={selectStyles}
                  />
                </FilterField>
              </>
            )}
            {view === "archived" && (
              <FilterField label="Blocked for" md={5}>
                <Form.Select
                  size="sm"
                  value={archivedReason}
                  onChange={(e) => {
                    setPage(1);
                    setArchivedReason(e.target.value);
                  }}
                >
                  <option value="">All reasons</option>
                  {(blockReasons ?? []).map((r) => (
                    <option key={r._id} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </Form.Select>
              </FilterField>
            )}
            <FilterField label="Country" md={2}>
              <Select
                options={countryOptions}
                value={
                  countryDraft ? { value: countryDraft, label: countryDraft } : null
                }
                onChange={(o) => setCountryDraft(o?.value ?? "")}
                isClearable
                placeholder="All…"
                styles={selectStyles}
              />
            </FilterField>
            <FilterField
              label="Domain / brand"
              md={3}
              tooltip="Searches every bucket, not just the open one."
            >
              <Form.Control
                size="sm"
                placeholder="Search…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </FilterField>
            <FilterField
              label="QA list"
              md={3}
              tooltip="Applies straight away and narrows the bucket counts too."
            >
              <Select
                options={listOptions}
                value={listOptions.find((o) => o.value === qaListId) ?? null}
                onChange={(o) => {
                  setPage(1);
                  setQaListId(o?.value ?? "");
                }}
                isClearable
                placeholder="All lists…"
                styles={selectStyles}
              />
            </FilterField>
            <FilterField label="Sort by" md={2}>
              <Form.Select
                size="sm"
                value={sort}
                onChange={(e) => {
                  setPage(1);
                  setSort(e.target.value as WorkPipelineSort);
                }}
              >
                {Object.values(WorkPipelineSort).map((s) => (
                  <option key={s} value={s}>
                    {WORK_PIPELINE_SORT_LABELS[s]}
                  </option>
                ))}
              </Form.Select>
            </FilterField>
            {((view === "board" &&
              activeStage === WorkPipelineStage.TemplateBuilding) ||
              view === "agent-requests") && (
                <FilterField
                  label="Stuck reason"
                  md={3}
                  tooltip="Why the agent's walk is stuck on this merchant. Template broke and Run errored are reviewed here first; escalate to a dev with Need assistance when the tool is at fault."
                >
                  <Form.Select
                    size="sm"
                    value={stuckReason}
                    onChange={(e) => {
                      setPage(1);
                      setStuckReason(e.target.value);
                    }}
                  >
                    {view === "agent-requests" ? (
                      <option value="">Any agent request</option>
                    ) : (
                      <>
                        <option value="">All rows</option>
                        <option value="any">Any agent request</option>
                      </>
                    )}
                    {STUCK_REASONS.map((k) => (
                      <option key={k} value={k}>
                        {HELP_KIND_META[k].label}
                      </option>
                    ))}
                  </Form.Select>
                </FilterField>
              )}
            {view === "board" && (
              <>
                <FilterField
                  label="Stage set"
                  md={2}
                  tooltip="Pinned rows were moved by a manager and don't re-derive from data."
                >
                  <Form.Select
                    size="sm"
                    value={pinned}
                    onChange={(e) => {
                      setPage(1);
                      setPinned(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    <option value="pinned">Pinned (manual)</option>
                    <option value="auto">Auto (derived)</option>
                  </Form.Select>
                </FilterField>
                <FilterField
                  label="Not checked in"
                  md={2}
                  tooltip="Rows nobody has worked for this long, including never."
                >
                  <Form.Select
                    size="sm"
                    value={notCheckedDays}
                    onChange={(e) => {
                      setPage(1);
                      setNotCheckedDays(e.target.value);
                    }}
                  >
                    <option value="">Any time</option>
                    <option value="3">3+ days</option>
                    <option value="7">7+ days</option>
                    <option value="14">14+ days</option>
                    <option value="30">30+ days</option>
                  </Form.Select>
                </FilterField>
                <FilterField label="Agentic" md={3}>
                  <Form.Check
                    type="checkbox"
                    id="wp-exclude-agentic"
                    className="small"
                    label="Hide domains already covered by agentic testing"
                    checked={excludeAgentic}
                    onChange={(e) => {
                      setPage(1);
                      setExcludeAgentic(e.target.checked);
                    }}
                  />
                </FilterField>
              </>
            )}
          </FilterBar>

          {staleListFilter && (
            <Alert variant="warning" className="py-2 small mb-3">
              The QA list this board is filtered to no longer has any rows on
              the pipeline, so nothing will show.{" "}
              <Button
                size="sm"
                variant="link"
                className="p-0 align-baseline"
                onClick={() => {
                  setPage(1);
                  setQaListId("");
                }}
              >
                Show all lists
              </Button>
            </Alert>
          )}

          {selectedList && (
            <Alert variant="light" className="border py-2 small mb-3">
              <strong>{selectedList.qaListName}</strong> — board scoped to this
              list.{" "}
              <strong>
                {selectedList.complete}/{selectedList.total}
              </strong>{" "}
              complete
              {selectedList.total > selectedList.complete && (
                <>
                  {" "}
                  · {selectedList.total - selectedList.complete} still to work
                </>
              )}
              .
            </Alert>
          )}

          {recomputeMessage && (
            <Alert
              variant="info"
              dismissible
              onClose={() => setRecomputeMessage(null)}
              className="py-2"
            >
              {recomputeMessage}
            </Alert>
          )}

          {actionError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setActionError(null)}
              className="py-2"
            >
              {actionError}
            </Alert>
          )}

          {view === "agent-dismissed" ? (
            <DismissedAgentRequests
              search={search}
              canReopen={isManager}
              onChanged={refreshAll}
            />
          ) : (
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">
                {pag
                  ? `${pag.total} item${pag.total === 1 ? "" : "s"} ${
                      view === "board"
                        ? search
                          ? "search results across all buckets"
                          : assignee
                            ? "currently claimed by the selected tester"
                            : `in ${WORK_PIPELINE_STAGE_LABELS[activeStage]}`
                        : view === "urgent"
                          ? "marked urgent by a lead, across every bucket"
                          : view === "agent-requests"
                          ? "with an open agent request, across every bucket"
                          : view === "mine"
                          ? "that I claimed or fixed"
                          : view === "help"
                            ? "awaiting assistance feedback"
                            : view === "deals"
                              ? "in the deals queue"
                              : view === "review"
                                ? "awaiting a block decision"
                                : view === "templates"
                                  ? "with a disabled auto or SKU template (tracking only)"
                                  : "archived"
                    }`
                  : ""}
              </span>
              {view === "board" &&
                activeStage === WorkPipelineStage.AwaitingSnapshot && (
                  <Button
                    size="sm"
                    variant="info"
                    onClick={() =>
                      snapshotIds((board?.results ?? []).map((r) => r._id))
                    }
                    disabled={snapshotReq.loading || !board?.results?.length}
                  >
                    <UploadCloud size={14} className="me-1" /> Add all to
                    snapshot
                  </Button>
                )}
            </Card.Header>

            <Card.Body className="p-0">
              <style>{`
            .work-pipeline-board-items thead { display: none; }
            .work-pipeline-board-items, .work-pipeline-board-items tbody { display: block; }
            .work-pipeline-board-items tbody { padding: 0.75rem; }
            .work-pipeline-board-items tbody tr {
              display: grid;
              gap: 0.35rem 1rem;
              margin-bottom: 0.75rem;
              padding: 1rem;
              border: 1px solid var(--bs-border-color);
              border-radius: 0.375rem;
              background: var(--bs-body-bg) !important;
            }
            .work-pipeline-board-items tbody tr:last-child { margin-bottom: 0; }
            .work-pipeline-board-items tbody td {
              display: block;
              padding: 0 !important;
              border: 0 !important;
              background: transparent !important;
            }
            .work-pipeline-board-items tbody td:last-child { text-align: right; }
            .work-pipeline-board-manager-items tbody tr {
              grid-template-columns: 1.25rem minmax(11rem, 1fr) minmax(16rem, 1.4fr) auto auto;
              grid-template-areas: "select identity status codes actions" "select block status codes actions";
            }
            .work-pipeline-board-manager-items tbody td:nth-child(1) { grid-area: select; align-self: center; }
            .work-pipeline-board-manager-items tbody td:nth-child(2) { grid-area: identity; }
            .work-pipeline-board-manager-items tbody td:nth-child(3) { grid-area: block; }
            .work-pipeline-board-manager-items tbody td:nth-child(4) { grid-area: status; }
            .work-pipeline-board-manager-items tbody td:nth-child(5) { grid-area: codes; align-self: center; }
            .work-pipeline-board-manager-items tbody td:nth-child(6) { grid-area: actions; align-self: center; }
            .work-pipeline-board-tester-items tbody tr {
              grid-template-columns: minmax(11rem, 1fr) minmax(16rem, 1.4fr) auto auto;
              grid-template-areas: "identity status codes actions" "block status codes actions";
            }
            .work-pipeline-board-tester-items tbody td:nth-child(1) { grid-area: identity; }
            .work-pipeline-board-tester-items tbody td:nth-child(2) { grid-area: block; }
            .work-pipeline-board-tester-items tbody td:nth-child(3) { grid-area: status; }
            .work-pipeline-board-tester-items tbody td:nth-child(4) { grid-area: codes; align-self: center; }
            .work-pipeline-board-tester-items tbody td:nth-child(5) { grid-area: actions; align-self: center; }
            @media (max-width: 991px) {
              .work-pipeline-board-items tbody tr,
              .work-pipeline-board-manager-items tbody tr,
              .work-pipeline-board-tester-items tbody tr {
                grid-template-columns: 1fr auto;
                grid-template-areas: "identity select" "status status" "block block" "codes actions";
              }
              .work-pipeline-board-manager-items tbody td:nth-child(1) { grid-area: select; }
              .work-pipeline-board-manager-items tbody td:nth-child(2),
              .work-pipeline-board-tester-items tbody td:nth-child(1) { grid-area: identity; }
              .work-pipeline-board-manager-items tbody td:nth-child(3),
              .work-pipeline-board-tester-items tbody td:nth-child(2) { grid-area: block; }
              .work-pipeline-board-manager-items tbody td:nth-child(4),
              .work-pipeline-board-tester-items tbody td:nth-child(3) { grid-area: status; }
              .work-pipeline-board-manager-items tbody td:nth-child(5),
              .work-pipeline-board-tester-items tbody td:nth-child(4) { grid-area: codes; }
              .work-pipeline-board-manager-items tbody td:nth-child(6),
              .work-pipeline-board-tester-items tbody td:nth-child(5) { grid-area: actions; }
            }
          `}</style>
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" size="sm" /> Loading…
                </div>
              ) : !board?.results?.length ? (
                <div className="text-center text-muted p-4">
                  {view === "board"
                    ? "Nothing in this stage"
                    : view === "urgent"
                      ? "Nothing on the board is marked urgent"
                    : view === "agent-requests"
                      ? "No open agent requests"
                    : view === "review"
                      ? "No blocks waiting for review"
                      : view === "templates"
                        ? "No disabled templates on the board"
                        : "Nothing archived"}
                  {hasActiveFilters ? " for these filters" : ""}.
                </div>
              ) : (
                <Table
                  hover={view !== "board"}
                  className={`mb-0 align-middle ${
                    view === "board"
                      ? isManager
                        ? "work-pipeline-board-items work-pipeline-board-manager-items"
                        : "work-pipeline-board-items work-pipeline-board-tester-items"
                      : ""
                  }`}
                >
                  <thead>
                    <tr>
                      {view === "board" && isManager && <th />}
                      <th>Brand / Domain</th>
                      <th>
                        {view === "board"
                          ? "Block brand"
                          : view === "review"
                            ? "Block request"
                            : view === "help" || view === "deals"
                              ? "Issue"
                              : view === "urgent"
                                ? "Why urgent"
                              : view === "agent-requests"
                                ? "Agent request"
                              : view === "templates"
                                ? "Disabled templates"
                                : "Blocked for"}
                      </th>
                      {view === "board" && <th>Pipeline status</th>}
                      {view !== "board" && view !== "archived" && (
                        <th>Waiting</th>
                      )}
                      <th>Codes</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {board.results.map((item) => {
                      const claimedByMe =
                        item.assignedTo &&
                        item.assignedTo === user?._id?.toString();
                      const myDevWork =
                        !!user?._id &&
                        (item.devClaimedBy === user._id.toString() ||
                          item.devOwner === user._id.toString());
                      // In Dev fixes the row belongs to whichever dev claimed it, not the tester waiting to retest.
                      const devBucket =
                        WORK_PIPELINE_DEV_CLAIM_STAGES.includes(item.stage) &&
                        !item.devFeedbackAt;
                      const claimedByOther = devBucket
                        ? !!item.devClaimedBy && !myDevWork
                        : item.assignedTo && !claimedByMe && !myDevWork;
                      // Deep-link to the full-auto-coupons targeting/testing page
                      // whenever we have the tracking id — in every stage, new tab.
                      const facLink = item.facItemId
                        ? `/kindred/full-auto-coupons-targeting/${item.facItemId}`
                        : null;
                      return (
                        <tr
                          key={item._id}
                          style={{
                            opacity: claimedByOther ? 0.55 : 1,
                            background: claimedByOther ? "#f6f6f6" : undefined,
                          }}
                        >
                          {view === "board" && isManager && (
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedIds.includes(item._id)}
                                onChange={() =>
                                  setSelectedIds((current) =>
                                    current.includes(item._id)
                                      ? current.filter((id) => id !== item._id)
                                      : [...current, item._id]
                                  )
                                }
                              />
                            </td>
                          )}
                          <td>
                            {facLink ? (
                              <Link
                                to={facLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="fw-semibold"
                              >
                                {item.brandName || item.domain}{" "}
                                <ExternalLink size={12} />
                              </Link>
                            ) : (
                              <span className="fw-semibold">
                                {item.brandName || item.domain}
                              </span>
                            )}
                            {item.brandName && (
                              <div>
                                <small className="text-muted">
                                  {item.domain}
                                </small>
                              </div>
                            )}
                            <Badge bg="light" text="dark" className="mt-1 me-1">
                              {item.countryCode}
                            </Badge>
                            <UrgentToggle
                              domain={item.domain}
                              countryCode={item.countryCode}
                              urgent={item.urgent}
                              canEdit={isManager && view !== "archived"}
                              onChanged={refreshAll}
                              className="me-1"
                            />
                            {view !== "templates" && (
                              <TemplateDisabledTags item={item} />
                            )}
                            <OtherListsTag item={item} />
                            {item.assignedToName && view !== "archived" && (
                              <small className="text-muted d-block mt-1">
                                <UserIcon size={12} className="me-1" />
                                {(users ?? []).find(
                                  (pipelineUser) =>
                                    pipelineUser._id.toString() ===
                                    item.assignedTo
                                )?.username ?? item.assignedToName}
                              </small>
                            )}
                            {(search ||
                              assignee ||
                              view === "mine" ||
                              view === "urgent" ||
                              view === "agent-requests" ||
                              view === "templates") && (
                              <Badge
                                bg={STAGE_VARIANT[item.stage]}
                                className="mt-1"
                              >
                                {WORK_PIPELINE_STAGE_LABELS[item.stage]}
                              </Badge>
                            )}
                            {item.block?.state ===
                              WorkPipelineBlockState.Proposed && (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="mt-1"
                                title={item.block.note ?? undefined}
                              >
                                <Clock size={11} className="me-1" />
                                Block pending
                                {item.block.reasonLabel
                                  ? ` · ${item.block.reasonLabel}`
                                  : ""}
                              </Badge>
                            )}
                            {item.block?.state ===
                              WorkPipelineBlockState.Rejected && (
                              <Badge
                                bg="light"
                                text="dark"
                                className="mt-1"
                                title={item.block.reviewNote ?? undefined}
                              >
                                Block rejected
                              </Badge>
                            )}
                            {item.escalatedTo && view !== "help" && (
                              <Badge
                                bg="info"
                                text="dark"
                                className="mt-1"
                                title={item.assistance?.issue ?? undefined}
                              >
                                Escalated to Help
                              </Badge>
                            )}
                            {item.dealMismatchAt && view !== "deals" && (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="mt-1"
                                title={item.dealMismatchNote ?? undefined}
                              >
                                Escalated to Deals ·{" "}
                                {item.dealMismatchByName ?? "Tester"}
                              </Badge>
                            )}
                            {item.stage === WorkPipelineStage.DevFixes &&
                              !item.devFeedbackAt && (
                                <div className="mt-1 d-flex flex-wrap gap-1">
                                  {item.devStatus ? (
                                    <Badge
                                      bg={
                                        item.devStatus ===
                                        WorkPipelineDevStatus.Blocked
                                          ? "danger"
                                          : "info"
                                      }
                                      text={
                                        item.devStatus ===
                                        WorkPipelineDevStatus.Blocked
                                          ? undefined
                                          : "dark"
                                      }
                                      title={
                                        item.devStatusAt
                                          ? `Set ${new Date(item.devStatusAt).toLocaleString()}`
                                          : undefined
                                      }
                                    >
                                      Dev:{" "}
                                      {WORK_PIPELINE_DEV_STATUS_LABELS[item.devStatus]}{" "}
                                      · {item.devStatusByName ?? "dev"}
                                    </Badge>
                                  ) : (
                                    <Badge
                                      bg="danger"
                                      title={
                                        item.devEscalatedByName
                                          ? `${item.devEscalatedByName} marked it broken and keeps the claim to retest`
                                          : "Escalated to dev — waiting for a dev to pick it up"
                                      }
                                    >
                                      {item.devClaimedByName
                                        ? `With dev · ${item.devClaimedByName}`
                                        : WORK_PIPELINE_DEV_ESCALATED_LABEL}
                                    </Badge>
                                  )}
                                  {item.devStatus && item.devClaimedByName && (
                                    <Badge bg="light" text="dark" className="border">
                                      With dev · {item.devClaimedByName}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            {item.stage === WorkPipelineStage.DevFixes &&
                              !item.devFeedbackAt &&
                              item.devFeedback && (
                                <small className="d-block text-muted mt-1">
                                  {item.devFeedback}
                                </small>
                              )}
                            {item.devFeedbackAt && (
                              <Alert
                                variant="danger"
                                className="py-1 px-2 mb-0 mt-2 small"
                              >
                                <strong>
                                  Dev:{" "}
                                  {item.devStatus
                                    ? WORK_PIPELINE_DEV_STATUS_LABELS[item.devStatus]
                                    : "Ready for testing"}{" "}
                                  ({item.devFeedbackByName || "dev"}) —{" "}
                                  {devHandbackNext(item.devStatus)}
                                </strong>{" "}
                                {item.devFeedback}
                              </Alert>
                            )}
                            {!devBucket && item.devClaimedByName && (
                              <small
                                className="text-muted d-block mt-1"
                                title="The dev keeps this fix while it is retested; if it fails again it goes straight back to them."
                              >
                                <UserIcon size={12} className="me-1" />
                                Dev: {item.devClaimedByName}
                                {canDevWork && (
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 ms-2 small align-baseline"
                                    disabled={devReq.loading}
                                    title="Let go of this fix; a later failure goes to whichever dev claims it"
                                    onClick={() =>
                                      devAction(item._id, "dev-release")
                                    }
                                  >
                                    Release
                                  </Button>
                                )}
                              </small>
                            )}
                            <DevHistory history={item.devHistory ?? []} />
                            {item.leadClaimedByName && (
                              <small className="text-muted d-block mt-1">
                                <Briefcase size={12} className="me-1" />
                                Lead: {item.leadClaimedByName}
                              </small>
                            )}
                            {item.assistance?.feedback && (
                              <Alert
                                variant="info"
                                className="py-1 px-2 mb-0 mt-2 small"
                              >
                                <strong>
                                  Feedback from{" "}
                                  {item.assistance?.respondedByName || "lead"}:
                                </strong>{" "}
                                {item.assistance?.feedback}
                              </Alert>
                            )}
                            {item.dealFeedback && (
                              <Alert
                                variant="warning"
                                className="py-1 px-2 mb-0 mt-2 small"
                              >
                                <strong>
                                  Deals feedback from{" "}
                                  {item.dealFeedbackByName || "Deals"}:
                                </strong>{" "}
                                {item.dealFeedback}
                              </Alert>
                            )}
                          </td>
                          <td>
                            {view === "help" ? (
                              <div style={{ maxWidth: 360 }}>
                                <div className="fw-semibold small">
                                  {item.assistance?.issue ||
                                    "No issue supplied"}
                                </div>
                                <small className="text-muted">
                                  Requested by{" "}
                                  {item.assistance?.requestedByName || "Tester"}
                                  {item.assistance?.requestedAt
                                    ? ` · ${new Date(item.assistance?.requestedAt).toLocaleString()}`
                                    : ""}
                                </small>
                              </div>
                            ) : view === "templates" ? (
                              <div style={{ maxWidth: 360 }}>
                                <TemplateDisabledTags item={item} />
                                <small className="text-muted d-block mt-1">
                                  {item.templatesDisabled?.byDev
                                    ? `Disabled by ${item.devStatusByName ?? "a dev"}; approved by ${item.devDisableApprovedByName ?? item.block?.reviewedByName ?? "a manager"}${
                                        (item.devDisableApprovedAt ?? item.block?.reviewedAt)
                                          ? ` · ${new Date((item.devDisableApprovedAt ?? item.block?.reviewedAt)!).toLocaleString()}`
                                          : ""
                                      }.`
                                    : "Every template row for this domain is inactive in SQL."}{" "}
                                  {item.archived
                                    ? "Archived off the board."
                                    : `Still in ${WORK_PIPELINE_STAGE_LABELS[item.stage]} and worked as normal.`}
                                </small>
                                {item.devFeedback &&
                                  item.templatesDisabled?.byDev && (
                                    <small className="d-block mt-1">
                                      {item.devFeedback}
                                    </small>
                                  )}
                                {isManager && item.devDisableApprovedAt && (
                                  <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 mt-1 small"
                                    disabled={reenableReq.loading}
                                    title="The template works again: take the dev's disabled tag off this domain. A template switched off in SQL keeps its tag until it is switched back on there."
                                    onClick={() => markTemplateReenabled(item._id)}
                                  >
                                    Mark re-enabled
                                  </Button>
                                )}
                              </div>
                            ) : view === "agent-requests" ? (
                              <div style={{ maxWidth: 360 }}>
                                <TemplateHelpBadge help={item.templateHelp} />
                                <div className="small mt-1">
                                  {item.templateHelp?.errorMessage ?? (
                                    <span className="text-muted">No message recorded</span>
                                  )}
                                </div>
                                {item.templateHelp?.lastFailedAt && (
                                  <small className="text-muted">
                                    Last failed{" "}
                                    {formatDistanceToNow(new Date(item.templateHelp.lastFailedAt), {
                                      addSuffix: true,
                                    })}{" "}
                                    · {item.templateHelp.failureCount} failure
                                    {item.templateHelp.failureCount === 1 ? "" : "s"}
                                  </small>
                                )}
                              </div>
                            ) : view === "urgent" ? (
                              <div style={{ maxWidth: 360 }}>
                                <div className="fw-semibold small">
                                  {item.urgent?.note || "No reason given"}
                                </div>
                                {item.urgent && (
                                  <small className="text-muted">
                                    {item.urgent.setByName ?? "A lead"} ·{" "}
                                    {formatDistanceToNow(new Date(item.urgent.setAt), {
                                      addSuffix: true,
                                    })}
                                  </small>
                                )}
                              </div>
                            ) : view === "deals" ? (
                              <div style={{ maxWidth: 360 }}>
                                <div className="fw-semibold small">
                                  {item.dealMismatchNote || "No issue supplied"}
                                </div>
                                <small className="text-muted">
                                  Escalated by{" "}
                                  {item.dealMismatchByName || "Tester"}
                                  {item.dealMismatchAt
                                    ? ` · ${new Date(item.dealMismatchAt).toLocaleString()}`
                                    : ""}
                                </small>
                              </div>
                            ) : view === "board" ? (
                              <Form.Select
                                size="sm"
                                style={{ minWidth: 160 }}
                                value=""
                                disabled={
                                  blockReq.loading ||
                                  item.block?.state ===
                                    WorkPipelineBlockState.Proposed
                                }
                                onChange={(e) =>
                                  e.target.value &&
                                  openBlockModal(item, e.target.value)
                                }
                              >
                                <option value="">
                                  {item.block?.state ===
                                  WorkPipelineBlockState.Proposed
                                    ? "Awaiting review…"
                                    : "Propose block…"}
                                </option>
                                {activeReasons.map((r) => (
                                  <option key={r._id} value={r.code}>
                                    {r.label}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : (
                              <div style={{ maxWidth: 320 }}>
                                <div className="fw-semibold small">
                                  {item.block?.reasonLabel ?? "—"}
                                </div>
                                {item.block?.note && (
                                  <div>
                                    <small className="text-muted">
                                      {item.block.note}
                                    </small>
                                  </div>
                                )}
                                <small className="text-muted">
                                  {item.block?.proposedByName ?? "Unknown"}
                                  {item.block?.proposedAt
                                    ? ` · ${new Date(item.block.proposedAt).toLocaleString()}`
                                    : ""}
                                </small>
                                {view === "archived" &&
                                  item.block?.reviewedAt && (
                                    <div>
                                      <small className="text-muted">
                                        Archived by{" "}
                                        {item.block.reviewedByName ??
                                          "a manager"}{" "}
                                        ·{" "}
                                        {new Date(
                                          item.block.reviewedAt
                                        ).toLocaleString()}
                                      </small>
                                    </div>
                                  )}
                              </div>
                            )}
                          </td>
                          {view === "board" && (
                            <td>
                              <small>{item.stageReason}</small>
                              {(item.templateHelp || item.templateRuns?.length > 0) && (
                                <div className="mt-1 d-flex flex-wrap gap-1">
                                  <TemplateHelpBadge help={item.templateHelp} />
                                  <TemplateRunBadge item={item} />
                                </div>
                              )}
                              {item.releaseNote && !item.assignedTo && (
                                <div
                                  className="small text-warning mt-1"
                                  title={
                                    item.releaseNoteAt
                                      ? `Released ${new Date(item.releaseNoteAt).toLocaleString()}`
                                      : undefined
                                  }
                                >
                                  Released by{" "}
                                  {item.releaseNoteByName ?? "a tester"}:{" "}
                                  {item.releaseNote}
                                </div>
                              )}
                              <div className="mt-1 d-flex flex-wrap gap-1 align-items-center">
                                <Badge
                                  bg={SNAPSHOT_VARIANT[item.snapshotState]}
                                >
                                  {item.snapshotState}
                                </Badge>
                                <small className="text-muted">
                                  Waiting{" "}
                                  {formatDistanceToNow(
                                    new Date(item.waitingSince),
                                    { addSuffix: true }
                                  )}
                                </small>
                                <small
                                  className={
                                    item.lastCheckedAt
                                      ? "text-muted"
                                      : "text-warning"
                                  }
                                >
                                  Checked{" "}
                                  {item.lastCheckedAt
                                    ? formatDistanceToNow(
                                        new Date(item.lastCheckedAt),
                                        { addSuffix: true }
                                      )
                                    : "never"}
                                </small>
                                {item.devFailType && (
                                  <Badge bg="secondary">
                                    {item.devFailType === "checkout-extension"
                                      ? "Checkout Ext"
                                      : "APK"}
                                  </Badge>
                                )}
                                {(item.stage === WorkPipelineStage.ApkTesting ||
                                  item.stage ===
                                    WorkPipelineStage.DevFixes) && (
                                  <Badge
                                    bg={
                                      item.lastSdkTestStatus === "green"
                                        ? "success"
                                        : item.lastSdkTestResult === "fail"
                                          ? "danger"
                                          : item.lastSdkTestAt
                                            ? "warning"
                                            : "secondary"
                                    }
                                    text={
                                      item.lastSdkTestStatus !== "green" &&
                                      item.lastSdkTestResult !== "fail"
                                        ? "dark"
                                        : undefined
                                    }
                                  >
                                    {item.lastSdkTestAt
                                      ? `APK ${
                                          item.lastSdkTestResult === "pass"
                                            ? "pass"
                                            : "fail"
                                        } · ${formatDistanceToNow(
                                          new Date(item.lastSdkTestAt),
                                          { addSuffix: true }
                                        )}`
                                      : "APK not tested"}
                                  </Badge>
                                )}
                                {item.manualOverride && (
                                  <Badge bg="dark">pinned</Badge>
                                )}
                              </div>
                            </td>
                          )}
                          {view !== "board" && view !== "archived" && (
                            <td>
                              <small>
                                {formatDistanceToNow(
                                  new Date(item.waitingSince),
                                  { addSuffix: true }
                                )}
                              </small>
                            </td>
                          )}
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openDrawer(item._id)}
                            >
                              Codes ›
                            </Button>
                          </td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-1 align-items-center">
                              {view === "help" || view === "deals" ? (
                                (
                                  view === "help"
                                    ? isAdmin || isLeadTester
                                    : isManager
                                ) ? (
                                  <>
                                    {!item.leadClaimedBy ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        disabled={leadClaimReq.loading}
                                        title="Take this escalation as lead. The tester keeps their claim."
                                        onClick={() => leadClaim(item._id)}
                                      >
                                        Claim as Lead
                                      </Button>
                                    ) : item.leadClaimedBy ===
                                      user?._id?.toString() ? (
                                      <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        disabled={leadClaimReq.loading}
                                        title="Hand this escalation back so another lead can take it."
                                        onClick={() => leadRelease(item._id)}
                                      >
                                        Release lead claim
                                      </Button>
                                    ) : null}
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      title="Reply to the tester and close the escalation. They keep the claim and finish the test."
                                      onClick={() => {
                                        setFeedbackFor(item);
                                        setFeedbackQueue(view);
                                        setAssistanceFeedback("");
                                      }}
                                    >
                                      Send feedback
                                    </Button>
                                  </>
                                ) : (
                                  <small className="text-muted">
                                    Awaiting{" "}
                                    {view === "help" ? "lead" : "Deals"}{" "}
                                    feedback
                                  </small>
                                )
                              ) : view === "review" ? (
                                isManager ? (
                                  <ButtonGroup size="sm">
                                    <Button
                                      variant="outline-secondary"
                                      disabled={blockReq.loading}
                                      onClick={() =>
                                        openBlockModal(
                                          item,
                                          item.block?.reasonCode ?? ""
                                        )
                                      }
                                    >
                                      Edit reason
                                    </Button>
                                    <Button
                                      variant="success"
                                      disabled={decisionReq.loading}
                                      title={
                                        item.block?.reasonCode ===
                                        WORK_PIPELINE_DEV_DISABLED_REASON.code
                                          ? "Keep the domain on the board and tag its template disabled; it shows in Disabled templates"
                                          : "Archive the domain off the board"
                                      }
                                      onClick={() =>
                                        decideBlock(item._id, true)
                                      }
                                    >
                                      <Archive size={13} className="me-1" />{" "}
                                      Approve
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      disabled={decisionReq.loading}
                                      onClick={() => {
                                        setRejectFor(item);
                                        setReviewNote("");
                                      }}
                                    >
                                      <X size={13} className="me-1" /> Reject
                                    </Button>
                                  </ButtonGroup>
                                ) : (
                                  <small className="text-muted">
                                    Awaiting manager review
                                  </small>
                                )
                              ) : view === "archived" || item.archived ? (
                                isManager ? (
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    disabled={restoreReq.loading}
                                    onClick={() => restoreBlocked(item._id)}
                                  >
                                    <RotateCcw size={13} className="me-1" />{" "}
                                    Restore
                                  </Button>
                                ) : (
                                  <small className="text-muted">Archived</small>
                                )
                              ) : view === "board" &&
                                item.stage ===
                                  WorkPipelineStage.AwaitingSnapshot ? (
                                item.stage ===
                                WorkPipelineStage.AwaitingSnapshot ? (
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={() => snapshotIds([item._id])}
                                  >
                                    <UploadCloud size={13} /> Snapshot
                                  </Button>
                                ) : null
                              ) : item.stage === WorkPipelineStage.Complete ? (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  disabled={reopenReq.loading}
                                  onClick={() => reopen(item._id)}
                                >
                                  <RotateCcw size={13} className="me-1" />{" "}
                                  Reopen
                                </Button>
                              ) : item.stage === WorkPipelineStage.DevFixes &&
                                !item.devFeedbackAt &&
                                (canDevWork || item.devEscalatedAt) ? (
                                <>
                                  {canDevWork ? (
                                    <>
                                      {!item.devClaimedBy ? (
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          disabled={devReq.loading}
                                          title="Take this fix as dev. The tester keeps their claim and retests once you reply."
                                          onClick={() =>
                                            devAction(item._id, "dev-claim")
                                          }
                                        >
                                          Claim as dev
                                        </Button>
                                      ) : canDevWork ? (
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          disabled={devReq.loading}
                                          title={
                                            item.devClaimedBy ===
                                            user?._id?.toString()
                                              ? "Hand this fix back so another dev can take it"
                                              : `Take ${item.devClaimedByName ?? "the dev"} off this fix so another dev can take it`
                                          }
                                          onClick={() =>
                                            devAction(item._id, "dev-release")
                                          }
                                        >
                                          Release dev claim
                                        </Button>
                                      ) : null}
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        title="Set a status and comment, like the dev sheet. Ready for testing, Redo, Coupon issue, No deal and Disabled send it back to the tester to retest."
                                        onClick={() => {
                                          setDevFeedbackFor(item);
                                          setDevStatus(
                                            item.devStatus &&
                                              WORK_PIPELINE_DEV_STATUS_OPTIONS.includes(
                                                item.devStatus
                                              )
                                              ? item.devStatus
                                              : WorkPipelineDevStatus.InProgress
                                          );
                                          setDevFeedbackText("");
                                          setDevDisabledKinds([]);
                                          setDevFeedbackError(null);
                                        }}
                                      >
                                        Dev status
                                      </Button>
                                    </>
                                  ) : (
                                    <small className="text-muted">
                                      Escalated to dev
                                      {claimedByMe ? " · you keep the claim" : ""}
                                    </small>
                                  )}
                                  {isManager && item.assignedTo && !claimedByMe && (
                                    <Button
                                      size="sm"
                                      variant="outline-warning"
                                      disabled={unclaimReq.loading}
                                      onClick={() =>
                                        openRelease(item._id, "unclaim")
                                      }
                                      title="Take the claim off this tester so someone else retests it after the fix"
                                    >
                                      Remove claim
                                    </Button>
                                  )}
                                </>
                              ) : !item.assignedTo ? (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => claim(item._id)}
                                  disabled={
                                    WORK_PIPELINE_STAGE_OWNER[item.stage] ===
                                    "none"
                                  }
                                >
                                  Claim
                                </Button>
                              ) : claimedByMe ? (
                                <Dropdown as={ButtonGroup} align="end">
                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={
                                      outcomeReq.loading ||
                                      releaseReq.loading ||
                                      !!item.escalatedTo ||
                                      !!item.dealMismatchAt
                                    }
                                    title={
                                      item.escalatedTo || item.dealMismatchAt
                                        ? `Waiting for ${item.escalatedTo ? "Help" : "Deals"} feedback — finish once they reply`
                                        : "Mark this stage's work done; the row moves to the next bucket"
                                    }
                                    onClick={() =>
                                      recordOutcome(
                                        item._id,
                                        WorkPipelineOutcome.Completed
                                      )
                                    }
                                  >
                                    {pendingOutcome ===
                                    WorkPipelineOutcome.Completed ? (
                                      <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-1"
                                      />
                                    ) : (
                                      <Check size={13} />
                                    )}{" "}
                                    Done
                                  </Button>
                                  <Dropdown.Toggle
                                    split
                                    size="sm"
                                    variant="success"
                                    aria-label="More outcome options"
                                  />
                                  <Dropdown.Menu>
                                    {!item.escalatedTo &&
                                      !item.dealMismatchAt &&
                                      (!item.couponReviewStep ||
                                      (item.couponReviewStep === "apk" &&
                                        item.stage ===
                                          WorkPipelineStage.ApkTesting)) &&
                                    (item.stage ===
                                    WorkPipelineStage.ApkTesting ? (
                                      <>
                                        <Dropdown.Item
                                          onClick={() =>
                                            recordOutcome(
                                              item._id,
                                              WorkPipelineOutcome.Broken,
                                              {
                                                devFailType:
                                                  "checkout-extension",
                                              }
                                            )
                                          }
                                        >
                                          <AlertTriangle
                                            size={13}
                                            className="me-1 text-danger"
                                          />{" "}
                                          Checkout extension broken
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={() =>
                                            recordOutcome(
                                              item._id,
                                              WorkPipelineOutcome.Broken,
                                              { devFailType: "apk" }
                                            )
                                          }
                                        >
                                          <AlertTriangle
                                            size={13}
                                            className="me-1 text-danger"
                                          />{" "}
                                          APK broken
                                        </Dropdown.Item>
                                      </>
                                    ) : (
                                      <Dropdown.Item
                                        onClick={() =>
                                          recordOutcome(
                                            item._id,
                                            WorkPipelineOutcome.Broken
                                          )
                                        }
                                      >
                                        <AlertTriangle
                                          size={13}
                                          className="me-1 text-danger"
                                        />{" "}
                                        Broken
                                      </Dropdown.Item>
                                    ))}
                                    <Dropdown.Item
                                      title="Ask the Lead QA Testers for help; you keep the claim"
                                      onClick={() => {
                                        setHelpFor(item);
                                        setHelpIssue("");
                                      }}
                                    >
                                      <HelpCircle size={13} className="me-1" />{" "}
                                      Need assistance
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      title="Send a merge or mismatch issue to Deals; you keep the claim"
                                      disabled={!!item.dealMismatchAt}
                                      onClick={() => {
                                        setDealMismatchFor(item);
                                        setDealMismatchNote("");
                                      }}
                                    >
                                      <AlertTriangle
                                        size={13}
                                        className="me-1 text-warning"
                                      />{" "}
                                      Escalate to Deals
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      disabled={
                                        !!item.escalatedTo ||
                                        !!item.dealMismatchAt
                                      }
                                      onClick={() => release(item._id)}
                                      title="Give the claim back, with a note for the next person"
                                    >
                                      {item.escalatedTo || item.dealMismatchAt
                                        ? "Release claim (locked until feedback)"
                                        : "Release claim"}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              ) : (
                                <>
                                  <small className="text-muted">
                                    In progress
                                  </small>
                                  {isManager && (
                                    <Button
                                      size="sm"
                                      variant="outline-warning"
                                      disabled={unclaimReq.loading}
                                      onClick={() =>
                                        openRelease(item._id, "unclaim")
                                      }
                                      title="Take the claim off this tester so someone else can pick it up"
                                    >
                                      Remove claim
                                    </Button>
                                  )}
                                </>
                              )}

                              {/* Manager: move to any stage */}
                              {view === "board" && isManager && (
                                <Dropdown>
                                  <Dropdown.Toggle
                                    size="sm"
                                    variant="outline-secondary"
                                    id={`move-${item._id}`}
                                  >
                                    Move
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu align="end">
                                    {WORK_PIPELINE_STAGE_ORDER.filter(
                                      (s) => s !== item.stage
                                    ).map((s) => (
                                      <Dropdown.Item
                                        key={s}
                                        onClick={() => moveTo(item._id, s)}
                                      >
                                        {WORK_PIPELINE_STAGE_LABELS[s]}
                                      </Dropdown.Item>
                                    ))}
                                    {item.manualOverride && (
                                      <>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                          onClick={() =>
                                            moveTo(item._id, "auto")
                                          }
                                        >
                                          Auto (re-derive)
                                        </Dropdown.Item>
                                      </>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {pag && pag.totalPages > 1 && (
              <Card.Footer className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {(pag.page - 1) * pag.limit + 1}–
                  {Math.min(pag.page * pag.limit, pag.total)} of {pag.total}
                </small>
                <div className="d-flex gap-2 align-items-center">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <small className="text-muted">
                    Page {pag.page} of {pag.totalPages}
                  </small>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page >= pag.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </Card.Footer>
            )}
          </Card>
          )}
        </Col>
      </Row>

      <Modal show={!!releaseFor} onHide={() => setReleaseFor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {releaseFor?.mode === "unclaim" ? "Remove claim" : "Release claim"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            <strong>{releaseFor?.label}</strong> goes back on the board for
            anyone to claim. Say why, so the next person knows what they are
            picking up.
          </p>
          <Form.Label className="small mb-1">
            {releaseFor?.mode === "unclaim"
              ? "Why are you removing the claim?"
              : "Why are you releasing it?"}
            {releaseFor?.noteRequired ? "" : " (optional)"}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={releaseNote}
            onChange={(event) => setReleaseNote(event.target.value)}
            maxLength={1000}
            placeholder="e.g. Checkout needs a UK login I don't have."
          />
          {releaseError && (
            <Alert variant="danger" className="small mt-2 mb-0">
              {releaseError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setReleaseFor(null)}
            title="Keep the claim as it is"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              releaseReq.loading ||
              unclaimReq.loading ||
              (!!releaseFor?.noteRequired && !releaseNote.trim())
            }
            onClick={submitRelease}
            title="Give up the claim and leave the note on the row"
          >
            {releaseFor?.mode === "unclaim" ? "Remove claim" : "Release"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!devFeedbackFor}
        onHide={() => setDevFeedbackFor(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Dev status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            <strong>
              {devFeedbackFor?.brandName || devFeedbackFor?.domain}
            </strong>{" "}
            {devStatus === WorkPipelineDevStatus.Disabled
              ? "goes to Block review as a \"Disabled by dev\" request and the claim is released. Once a manager approves it, the domain stays on the board tagged with the disabled template and shows in Disabled templates."
              : devStatus === WorkPipelineDevStatus.CouponIssue
              ? "goes back to Coupon review for a code tester to fix the code, then to APK testing for the retest."
              : devStatus === WorkPipelineDevStatus.NoDeal
                ? `goes to the Deals queue with your comment; ${devFeedbackFor?.assignedToName ?? "the tester"} keeps the claim and retests in ${WORK_PIPELINE_STAGE_LABELS[devFeedbackFor?.devReturnStage ?? WorkPipelineStage.ApkTesting]} once Deals reply.`
                : devHandback
                  ? `goes back to ${devFeedbackFor?.assignedToName ?? "the testers"} in ${WORK_PIPELINE_STAGE_LABELS[devFeedbackFor?.devReturnStage ?? WorkPipelineStage.ApkTesting]} to retest.`
                  : "stays in Dev fixes with this status on it."}
            {devHandback &&
              " You keep the dev claim and your comment stays in its dev history; if it fails again it comes straight back to you."}
          </p>
          <Form.Label className="small mb-1">Status</Form.Label>
          <Form.Select
            size="sm"
            className="mb-3"
            value={devStatus}
            onChange={(e) =>
              setDevStatus(e.target.value as WorkPipelineDevStatus)
            }
            title="In progress keeps it with you. Ready for testing goes back to the APK tester, Coupon issue to Coupon review, Deals issue to the Deals queue, Disabled to Block review."
          >
            {WORK_PIPELINE_DEV_STATUS_OPTIONS.map((st) => (
              <option key={st} value={st}>
                {WORK_PIPELINE_DEV_STATUS_LABELS[st]}
                {st === WorkPipelineDevStatus.Disabled
                  ? " — to Block review"
                  : st === WorkPipelineDevStatus.CouponIssue
                  ? " — back to Coupon review"
                  : st === WorkPipelineDevStatus.NoDeal
                    ? " — to the Deals queue"
                    : WORK_PIPELINE_DEV_HANDBACK.includes(st)
                      ? " — send back to retest"
                      : ""}
              </option>
            ))}
          </Form.Select>
          {disabling && (
            <Form.Group className="mb-3">
              <Form.Label className="small mb-1">
                Which template did you disable?
              </Form.Label>
              {(["auto", "sku"] as const).map((kind) => (
                <Form.Check
                  key={kind}
                  type="checkbox"
                  id={`wp-dev-disabled-${kind}`}
                  label={WORK_PIPELINE_TEMPLATE_KIND_LABELS[kind]}
                  checked={devDisabledKinds.includes(kind)}
                  onChange={(e) =>
                    setDevDisabledKinds((current) =>
                      e.target.checked
                        ? [...current, kind]
                        : current.filter((k) => k !== kind)
                    )
                  }
                />
              ))}
              <Form.Text className="text-muted">
                Once a manager approves it, the domain stays on the board with
                this tag and shows in Disabled templates.
              </Form.Text>
            </Form.Group>
          )}
          <Form.Label className="small mb-1">Comment</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={devFeedbackText}
            onChange={(e) => setDevFeedbackText(e.target.value)}
            maxLength={4000}
            placeholder="e.g. Popup on cart — fixed the selector, released in 2.14."
          />
          {devFeedbackError && (
            <Alert variant="danger" className="small mt-2 mb-0">
              {devFeedbackError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDevFeedbackFor(null)}
            title="Close without saving"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              devReq.loading || (disabling && !devDisabledKinds.length)
            }
            onClick={submitDevFeedback}
            title={
              devStatus === WorkPipelineDevStatus.Disabled
                ? "Release the claim and propose a Disabled by dev block for a manager"
                : devHandback
                  ? "Save and hand the row back to a tester"
                  : "Save the status; the row stays in Dev fixes"
            }
          >
            {devStatus === WorkPipelineDevStatus.Disabled
              ? "Disable"
              : devStatus === WorkPipelineDevStatus.CouponIssue
              ? "Send to Coupon review"
              : devStatus === WorkPipelineDevStatus.NoDeal
                ? "Send to Deals"
                : devHandback
                  ? "Send back to retest"
                  : "Save status"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Needs-help escalation modal */}
      <Modal show={!!helpFor} onHide={() => setHelpFor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Escalate for help</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Ask the Lead QA Testers team to advise on{" "}
            <strong>{helpFor?.brandName || helpFor?.domain}</strong>. The domain
            stays claimed by you while awaiting feedback.
          </p>
          <Form.Label className="small mb-1">
            What do you need help with?
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            className="mb-3"
            value={helpIssue}
            onChange={(event) => setHelpIssue(event.target.value)}
            placeholder="Describe the issue and what you have already tried..."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setHelpFor(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!helpIssue.trim() || outcomeReq.loading}
            onClick={submitHelp}
          >
            Ask for help
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!feedbackFor} onHide={() => setFeedbackFor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Respond with feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-1">
            Requested by{" "}
            {(feedbackQueue === "deals"
              ? feedbackFor?.dealMismatchByName
              : feedbackFor?.assistance?.requestedByName) || "Tester"}
          </p>
          <Alert variant="light" className="border py-2">
            {(feedbackQueue === "deals"
              ? feedbackFor?.dealMismatchNote
              : feedbackFor?.assistance?.issue) || "No issue supplied"}
          </Alert>
          <Form.Label className="small mb-1">Advice / feedback</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={assistanceFeedback}
            onChange={(event) => setAssistanceFeedback(event.target.value)}
            placeholder="Explain what the tester should try next..."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFeedbackFor(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!assistanceFeedback.trim() || assistanceReq.loading}
            onClick={submitAssistanceFeedback}
          >
            Send back to tester
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!dealMismatchFor}
        onHide={() => setDealMismatchFor(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Escalate to Deals</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Send{" "}
            <strong>
              {dealMismatchFor?.brandName || dealMismatchFor?.domain}
            </strong>{" "}
            to the Deals queue for a merge or mismatch. The domain stays claimed
            by you; finish the test once Deals replies.
          </p>
          <Form.Label className="small mb-1">
            What needs merging or doesn&apos;t match?
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={dealMismatchNote}
            onChange={(event) => setDealMismatchNote(event.target.value)}
            placeholder="Describe the merge or mismatch issue..."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDealMismatchFor(null)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            disabled={!dealMismatchNote.trim() || outcomeReq.loading}
            onClick={submitDealMismatch}
          >
            Escalate to Deals
          </Button>
        </Modal.Footer>
      </Modal>

      <PipelineWorkbenchDrawer
        item={
          drawerItem
            ? {
                ...drawerItem,
                assignedToName:
                  (users ?? []).find(
                    (pipelineUser) =>
                      pipelineUser._id.toString() === drawerItem.assignedTo
                  )?.username ?? drawerItem.assignedToName,
              }
            : null
        }
        claimedByMe={
          !!drawerItem?.assignedTo &&
          drawerItem.assignedTo === user?._id?.toString()
        }
        busy={
          claimReq.loading ||
          releaseReq.loading ||
          outcomeReq.loading ||
          reopenReq.loading
        }
        onClose={() => clearDrawerParams(true)}
        onClaim={claim}
        onRelease={release}
        onReopen={reopen}
        onComplete={(id) => recordOutcome(id, WorkPipelineOutcome.Completed)}
        onBroken={(id, devFailType) =>
          recordOutcome(id, WorkPipelineOutcome.Broken, { devFailType })
        }
        onNeedsHelp={(item) => setHelpFor(item)}
        onDealMismatch={(item) => {
          setDealMismatchFor(item);
          setDealMismatchNote("");
        }}
        onBrandStatus={setBrandStatus}
        onProposeBlock={(row) => openBlockModal(row, "")}
        onStopRun={stopTemplateRun}
        onSnapshot={(id) => snapshotIds([id])}
        onHunt={recordHunt}
        huntBusy={huntReq.loading}
        onRefresh={refreshAll}
        actionError={actionError}
        onDismissError={() => setActionError(null)}
      />

      {/* Propose block (tester) */}
      <Modal show={!!blockFor} onHide={closeBlockModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Propose block</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Ask a QA manager to archive{" "}
            <strong>{blockFor?.brandName || blockFor?.domain}</strong>. It stays
            on the board until they decide.
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Reason</Form.Label>
            <Form.Select
              value={blockReasonCode}
              onChange={(e) => setBlockReasonCode(e.target.value)}
            >
              <option value="">Select a reason…</option>
              {activeReasons.map((r) => (
                <option key={r._id} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Form.Select>
            {activeReasons.find((r) => r.code === blockReasonCode)
              ?.description && (
              <Form.Text>
                {
                  activeReasons.find((r) => r.code === blockReasonCode)
                    ?.description
                }
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="What did you see?"
              value={blockNote}
              onChange={(e) => setBlockNote(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeBlockModal}>
            Cancel
          </Button>
          <Button
            variant="warning"
            disabled={!blockReasonCode || blockReq.loading}
            onClick={submitBlock}
          >
            {blockReq.loading && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Propose block
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject block (manager) */}
      <Modal show={!!rejectFor} onHide={() => setRejectFor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject block</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Send <strong>{rejectFor?.brandName || rejectFor?.domain}</strong>{" "}
            back to the board.
          </p>
          <Form.Label className="small mb-1">Review note (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Why is this still workable?"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectFor(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={decisionReq.loading}
            onClick={submitReject}
          >
            {decisionReq.loading && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Reject
          </Button>
        </Modal.Footer>
      </Modal>

      <ManageUrgentModal
        show={showUrgent}
        canEdit={isManager}
        onHide={() => setShowUrgent(false)}
        onChanged={refreshAll}
      />
      <MarkUrgentModal
        show={urgentTargets.length > 0}
        items={urgentTargets}
        onHide={() => setUrgentTargets([])}
        onDone={() => {
          setUrgentTargets([]);
          setSelectedIds([]);
          refreshAll();
        }}
      />
      <BlockReasonsModal
        show={showReasons}
        onHide={() => setShowReasons(false)}
        onSaved={refetchBlockReasons}
      />

      <Modal show={showQuickAdd} onHide={() => setShowQuickAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add domain to pipeline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Pipeline list</Form.Label>
            <Form.Select
              value={quickAddListId}
              onChange={(e) => setQuickAddListId(e.target.value)}
            >
              <option value="">Select a list...</option>
              {boardListIds.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Domain</Form.Label>
            <Form.Control
              placeholder="example.com"
              value={quickAddDomain}
              onChange={(e) => setQuickAddDomain(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Country code</Form.Label>
            <Form.Control
              placeholder="US"
              value={quickAddCountry}
              onChange={(e) => setQuickAddCountry(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuickAdd(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              !quickAddListId ||
              !quickAddDomain.trim() ||
              !quickAddCountry.trim() ||
              addDomainReq.loading
            }
            onClick={addDomain}
          >
            {addDomainReq.loading && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Add domain
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
